import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Formik, Form } from 'formik';
import { Grid, Typography, FormControlLabel, Switch, IconButton, Button as MUIButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';

import Modal from 'components/Modal';
import Loader from 'components/Loader';
import Input from 'components/Input';
import FileUploader from 'components/FileUploader';
import Button from 'components/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';

import { loadSelfServiceSettings, upsertSelfServiceSettings } from 'pages/SelfService/store/thunk';

import { getDominantColors } from 'helpers/common';

import { validations } from './validations';

const supportedLogoTypes = ['image/png', 'image/jpg', 'image/jpeg'];
const maxLogoSize = 5242880;

export default function SelfServiceCustomization({ open, onClose }) {
  const dispatch = useDispatch();

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isSettingsLoading, settings } = useSelector(state => state.selfService);
  const { pathLogo, urlLogo, primaryColor, secondaryColor, darkModeEnabled } = settings;

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');

  const initialValues = {
    primaryColor: primaryColor || '',
    secondaryColor: secondaryColor || '',
    darkModeEnabled: !!darkModeEnabled
  };

  useEffect(() => {
    if(establishmentId) {
      dispatch(loadSelfServiceSettings(userId, establishmentId));
    }
  }, [establishmentId]);

  useEffect(() => {
    setLogoPreviewUrl(urlLogo || pathLogo || '');
  }, [urlLogo, pathLogo]);

  const handleUploadLogo = async (e, setFieldValue) => {
    const file = e?.target?.files?.[0];

    if(!file) {
      return;
    }

    const { type, size } = file;

    if(!supportedLogoTypes.includes(type)) {
      return toast.error('O formato da imagem não é suportado.');
    }

    if(size > maxLogoSize) {
      return toast.error('O tamanho máximo da imagem não pode exceder 5 MB.');
    }

    try {
      const [primaryColor, secondaryColor] = await getDominantColors(file);

      setFieldValue('primaryColor', primaryColor?.hex);
      setFieldValue('secondaryColor', secondaryColor?.hex);
    } catch(_) {}

    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  }

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreviewUrl('');
  }

  const handleClose = () => {
    handleRemoveLogo();
    onClose();
  }

  const handleSubmit = values => {
    const params = {
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      darkModeEnabled: !!values.darkModeEnabled ? 1 : 0
    }

    dispatch(upsertSelfServiceSettings({ userId, establishmentId, logoFile, params }))
      .then(() => handleClose());
  }

  return(
    <Modal
      id='self-service-customization-modal'
      title='Personalização'
      maxWidth='md'
      open={open}
      onClose={handleClose}
    >
      <Loader isLoading={isSettingsLoading}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validations}
        >
          {({ values, errors, handleChange, setFieldValue }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography color='primary'>
                    <b>Faça o upload do seu logotipo</b>
                  </Typography>
                  <Typography color='textSecondary' style={{ margin: '10px 0 10px 0' }}>
                    Imagem utilizada nos totens de autoatendimento.
                    <br />
                    Apenas nos formatos: PNG, JPEG e JPG.
                  </Typography>
                </Grid>
                <Grid item md={3} sm={4} xs={12}>
                  <div>
                    {!!logoPreviewUrl ? (
                      <Grid container>
                        <Grid item xs={12} style={{ marginTop: 10 }}>
                          <img src={logoPreviewUrl} width='72' alt='Logo' />
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container>
                        <Grid
                          item
                          xs={12}
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '200px',
                            height: '165px',
                            backgroundColor: '#F5F5F5',
                            margin: '10px 0 10px 0'
                          }}
                        >
                          <FontAwesomeIcon
                            size='lg'
                            color='#1E5168'
                            icon={faUpload}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </div>
                  <FileUploader
                    fullWidth
                    id='logo'
                    name='logo'
                    file={logoFile}
                    onChange={event => handleUploadLogo(event, setFieldValue)}
                  >
                    Escolher ficheiro
                  </FileUploader>
                  {!!logoPreviewUrl && (
                    <Grid container>
                      <Grid item xs={12} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                        <img src={logoPreviewUrl} width="72" alt="Logo" />
                        <IconButton onClick={handleRemoveLogo}>
                          <Close />
                        </IconButton>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12} style={{ marginTop: 10 }}>
                  <Typography color='primary'>
                    <b>Defina o hexadecimal para personalizar cores</b>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Input
                    id='primaryColor'
                    name='primaryColor'
                    type='text'
                    label='Cor primária'
                    placeholder='#022A5C'
                    maxLength={7}
                    value={values.primaryColor}
                    error={!!errors.primaryColor}
                    helperText={errors.primaryColor}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Input
                    id='secondaryColor'
                    name='secondaryColor'
                    type='text'
                    label='Cor secundária'
                    placeholder='#FFFFFF'
                    maxLength={7}
                    value={values.secondaryColor}
                    error={!!errors.secondaryColor}
                    helperText={errors.secondaryColor}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    style={{ margin: 0 }}
                    labelPlacement='end'
                    label={
                      <Typography>
                        Ativar Dark Mode
                      </Typography>
                    }
                    control={
                      <Switch
                        color='primary'
                        name='darkModeEnabled'
                        checked={values.darkModeEnabled}
                        onChange={event => setFieldValue('darkModeEnabled', event.target.checked)}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <MUIButton
                    type='button'
                    color='primary'
                    variant='outlined'
                    onClick={handleClose}
                  >
                    Cancelar
                  </MUIButton>
                  <Button
                    type='submit'
                    color='success'
                    loading={isSettingsLoading}
                  >
                    Editar
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Loader>
    </Modal>
  );
}